export type OperationalRecommendationView = {
  key: string;
  decisionType: string;
  processId: number | null;
  routeId: number | null;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  reason: string;
  suggestedAction: string;
  suggestedRecipientDisplayName: string | null;
  status: string;
  governanceState?: 'informational' | 'recommended' | 'required' | 'blocked';
  governanceLabel?: string;
};

export type GovernanceViolationView = {
  id: number;
  violationType: string;
  severity: string;
  message: string;
  createdAt: string;
};

export type RouteDecisionSupportView = {
  recommendations: OperationalRecommendationView[];
  capacityPressure: Array<{
    department: string;
    pressureLevel: string;
    label: string;
    escalationRisk: boolean;
  }>;
  risks: Array<{ code: string; message: string; severity: string }>;
  governance?: {
    mandatoryActions: Array<{
      key: string;
      title: string;
      governanceState: string;
    }>;
    violations: GovernanceViolationView[];
  };
  compliance?: import('../compliance/complianceTypes').RouteComplianceView & {
    violations?: Array<{ message: string; severity: string; status: string }>;
  };
};

export function pickTopRecommendations(
  ds: RouteDecisionSupportView | null | undefined,
  limit = 4
): OperationalRecommendationView[] {
  if (!ds?.recommendations?.length) return [];
  const rank = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  return [...ds.recommendations]
    .filter((r) => r.status === 'GENERATED')
    .sort((a, b) => rank[a.priority] - rank[b.priority])
    .slice(0, limit);
}

function readGovernanceState(r: OperationalRecommendationView): string | undefined {
  return (
    r.governanceState ??
    (r as OperationalRecommendationView & { metadata?: { governanceState?: string } }).metadata
      ?.governanceState
  );
}

export function pickGovernanceRequired(
  ds: RouteDecisionSupportView | null | undefined,
  limit = 3
): OperationalRecommendationView[] {
  if (!ds?.recommendations?.length) return [];
  const rank = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  return [...ds.recommendations]
    .filter((r) => readGovernanceState(r) === 'required')
    .sort((a, b) => rank[a.priority] - rank[b.priority])
    .slice(0, limit);
}
